/**
 * Flow editor — minimap.
 *
 * A near top-down 3D camera onto the chart (NOT a 2D redraw): a small R3F canvas
 * with a high, slightly-tilted perspective camera looking straight down the
 * runway. The world group scrolls each frame so the current beat sits at the
 * HIT line, which is pinned to the BOTTOM edge of the minimap — matching the
 * editor's strike/trigger line. Notes render as horizontal PILLS (rounded
 * rectangles, like the real gameplay notes) and trails as SMOOTH curved rails
 * (Catmull-Rom through their waypoints) — the same visual logic as the highway.
 *
 * Surfaces/lines follow the theme (deck = tint, HIT = accent); note/trail
 * colours are the gameplay lane colours (meaningful, not themed).
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Canvas, useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { Line } from '@react-three/drei';
import * as THREE from 'three';
import { useFlowStore } from './flowStore';
import { useEditorStore } from '../../store/editorStore';
import { FLOW_LANE_COLORS, laneCountFor } from './types';
import { useTheme } from '../../components/ui/themeStore';
import { accentHex, tintHex } from '../../components/ui/themeColors';
import { minimapWrapStyle } from '../../components/ui/editorSharedStyles';

const ZPB = 0.6;              // world Z per beat (ahead = -Z)
const TRACK_W = 3.0;          // world width of the lane area
const FLOOR_LEN = 80;         // long static deck so it never runs out
// The HIT (trigger) line sits at z=0. The note world scrolls so the CURRENT
// beat lands exactly at z=0 too — i.e. the teal line always marks the trigger
// zone (where notes are "now"), at any camera angle. No fudge offset: a nonzero
// one would float the line off the current notes, which is visible once the
// camera tilts toward top-down.

function laneXFor(lane: number, laneCount: number): number {
  const laneW = TRACK_W / laneCount;
  return -TRACK_W / 2 + (lane + 0.5) * laneW;
}

/** A flat horizontal "pill" (rounded rectangle) geometry, lying in the XZ plane
 *  — the same silhouette as the gameplay notes seen from above. */
function pillGeometry(width: number, depth: number): THREE.ShapeGeometry {
  const r = Math.min(depth, width) / 2;
  const w = width, h = depth;
  const x = -w / 2, y = -h / 2;
  const s = new THREE.Shape();
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r);
  s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h);
  s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r);
  s.quadraticCurveTo(x, y, x + r, y);
  const geo = new THREE.ShapeGeometry(s);
  geo.rotateX(-Math.PI / 2); // lay flat (XY shape → XZ plane)
  return geo;
}

// How much RUNWAY the minimap can show, in world units (the deck is 80 long).
// Converted to beats per frame using the live zpb, so the Zoom slider keeps
// working: zoom out → more beats fit → wider window. Items outside never
// reach the GPU (the old code mounted a mesh — and a fat-line per trail — for
// EVERY note of the chart, the same ~200-draws/frame trap as the VR minimap).
const WIN_BEHIND_WORLD = 10;
const WIN_AHEAD_WORLD = 90;
/** Instance cap for the note-pill pool — one draw call regardless of density. */
const PILL_CAP = 512;

/** Module mirror of "potato tier is on", written by the component that reads
 *  the store (a plain useFrame can't subscribe). Lets the per-frame path skip
 *  work without a store read per frame. */
let POTATO_MINIMAP_IDLE = false;

/** Scene contents — scrolls the note world so the playhead sits at z=0 (HIT). */
function MinimapScene({ deck, hit, divider }: { deck: string; hit: string; divider: string }) {
  const chart = useFlowStore(s => s.chart);
  const laneColors = useFlowStore(s => s.gameplaySettings.laneColors);
  const laneCount = useFlowStore(s => laneCountFor(s.chart, s.activeDifficulty));
  // Adjustable compression (user feedback: notes packed too tightly) — the
  // Minimap Zoom slider (Minimap Options window) scales world-Z-per-beat.
  const zpb = ZPB * (useFlowStore(s => s.flowInterfaceSettings.minimapZoom) ?? 1);
  const worldRef = useRef<THREE.Group>(null);

  const laneW = TRACK_W / laneCount;
  // Pill: wide across the lane, thin along travel — like the real notes.
  const pillGeo = useMemo(() => pillGeometry(laneW * 0.62, 0.2), [laneW]);

  // ── Instanced note pills, windowed to the visible beats ──────────────────
  const pillMeshRef = useRef<THREE.InstancedMesh>(null);
  const noteData = useMemo(() => {
    const out: { beat: number; x: number; color: THREE.Color }[] = [];
    const push = (lane: number, beat: number) => out.push({
      beat,
      x: laneXFor(lane, laneCount),
      color: new THREE.Color(laneColors[lane] || FLOW_LANE_COLORS[lane] || '#ffffff'),
    });
    for (const n of chart.notes) {
      push(n.lane, n.time_beats);
      // A trail's CLOSING note is a real pill on the highway — mirror it here
      // too (user report: the tail note was missing from the minimap).
      const isTrail = (n.duration_beats && n.duration_beats > 0)
        || (n.waypoints && n.waypoints.length >= 2);
      if (isTrail) {
        const endLane = (n.waypoints && n.waypoints.length >= 2)
          ? n.waypoints[n.waypoints.length - 1].lane
          : n.lane;
        const endBeat = (n.waypoints && n.waypoints.length >= 2)
          ? n.waypoints[n.waypoints.length - 1].time_beats
          : n.time_beats + (n.duration_beats ?? 0);
        push(endLane, endBeat);
      }
    }
    return out.sort((a, b) => a.beat - b.beat);
  }, [chart.notes, laneCount, laneColors]);
  const m4 = useMemo(() => new THREE.Matrix4(), []);
  const lastWin = useRef('');
  // Chart/zoom/lane changes must force a rewrite even if the beat didn't move.
  useEffect(() => { lastWin.current = ''; }, [noteData, zpb]);

  // Trails still render as individual smooth curves (they're few), windowed via
  // a coarse 4-beat bucket so scrolling doesn't re-render React every frame.
  const [beatBucket, setBeatBucket] = React.useState(0);

  useFrame(() => {
    const beat = useFlowStore.getState().currentBeat;
    if (worldRef.current) worldRef.current.position.z = beat * zpb;
    // Potato: this is a SECOND WebGL context; while the music plays it's the
    // one thing on screen nobody edits, so stop feeding it work. (The scroll
    // above still runs — the map stays correct the moment playback stops.)
    if (POTATO_MINIMAP_IDLE && useFlowStore.getState().playing) return;

    const b = Math.floor(beat / 4);
    setBeatBucket(prev => (prev === b ? prev : b));

    const lo = beat - WIN_BEHIND_WORLD / zpb;
    const hi = beat + WIN_AHEAD_WORLD / zpb;
    const winKey = `${Math.round(lo * 2)}`;
    if (winKey === lastWin.current) return;      // window hasn't moved ≥ ½ beat
    lastWin.current = winKey;

    const pm = pillMeshRef.current;
    if (!pm) return;
    let lo2 = 0, hi2 = noteData.length;
    while (lo2 < hi2) { const m = (lo2 + hi2) >>> 1; if (noteData[m].beat < lo) lo2 = m + 1; else hi2 = m; }
    let w = 0;
    for (let i = lo2; i < noteData.length && noteData[i].beat <= hi && w < PILL_CAP; i++, w++) {
      const d = noteData[i];
      m4.makeTranslation(d.x, 0.03, -d.beat * zpb);
      pm.setMatrixAt(w, m4);
      pm.setColorAt(w, d.color);
    }
    pm.count = w;
    pm.instanceMatrix.needsUpdate = true;
    if (pm.instanceColor) pm.instanceColor.needsUpdate = true;
  });

  const visibleTrails = useMemo(() => {
    const beat = beatBucket * 4;
    const lo = beat - WIN_BEHIND_WORLD / zpb - 8;
    const hi = beat + WIN_AHEAD_WORLD / zpb + 8;
    return chart.notes.filter(n => {
      const isTrail = (n.duration_beats && n.duration_beats > 0)
        || (n.waypoints && n.waypoints.length >= 2);
      if (!isTrail) return false;
      const end = (n.waypoints && n.waypoints.length >= 2)
        ? n.waypoints[n.waypoints.length - 1].time_beats
        : n.time_beats + (n.duration_beats ?? 0);
      return n.time_beats <= hi && end >= lo;
    });
  }, [chart.notes, beatBucket, zpb]);

  return (
    <>
      <ambientLight intensity={1.5} />

      {/* Static deck + lane dividers (camera-fixed) */}
      <mesh position={[0, -0.01, -FLOOR_LEN / 2 + 4]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[TRACK_W, FLOOR_LEN]} />
        <meshBasicMaterial color={deck} />
      </mesh>
      {Array.from({ length: laneCount + 1 }).map((_, i) => (
        <mesh key={i} position={[-TRACK_W / 2 + laneW * i, 0.005, -FLOOR_LEN / 2 + 4]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.02, FLOOR_LEN]} />
          <meshBasicMaterial color={divider} transparent opacity={0.5} />
        </mesh>
      ))}

      {/* HIT line at z=0 (pinned to the bottom of the viewport) */}
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[TRACK_W + 0.3, 0.06]} />
        <meshBasicMaterial color={hit} />
      </mesh>

      {/* Notes (scrolling world): ONE instanced pool for every pill in the
          window + a smooth curve per visible trail. */}
      <group ref={worldRef}>
        <instancedMesh
          ref={pillMeshRef}
          args={[undefined, undefined, PILL_CAP]}
          geometry={pillGeo}
          frustumCulled={false}
        >
          {/* White base — setColorAt() multiplies, so the lane colour wins. */}
          <meshBasicMaterial />
        </instancedMesh>
        {visibleTrails.map(n => {
          const x = laneXFor(n.lane, laneCount);
          const wps = (n.waypoints && n.waypoints.length >= 2)
            ? n.waypoints
            : [
                { lane: n.lane, time_beats: n.time_beats },
                { lane: n.lane, time_beats: n.time_beats + (n.duration_beats ?? 0) },
              ];
          const raw = wps.map(w => new THREE.Vector3(
            (n.waypoints && n.waypoints.length >= 2) ? laneXFor(w.lane, laneCount) : x,
            0.018,
            -w.time_beats * zpb,
          ));
          const curve = new THREE.CatmullRomCurve3(raw, false, 'catmullrom', 0.5);
          const samples = Math.max(12, raw.length * 8);
          const pts = curve.getPoints(samples).map(p => [p.x, p.y, p.z] as [number, number, number]);
          // Same per-waypoint lane-colour gradient as the 3D highway trails
          // (user ask) — the line lerps between the lanes it passes through.
          const wpColors = wps.map(w =>
            new THREE.Color(laneColors[w.lane] ?? FLOW_LANE_COLORS[w.lane] ?? '#ffffff'));
          const vertexColors = pts.map((_, i) => {
            const t = i / Math.max(1, pts.length - 1);
            const segF = t * (wps.length - 1);
            const segIdx = Math.min(Math.floor(segF), wps.length - 2);
            return wpColors[segIdx].clone().lerp(wpColors[segIdx + 1], segF - segIdx);
          });
          return <Line key={n.id} points={pts} vertexColors={vertexColors} lineWidth={5} transparent opacity={0.7} />;
        })}
      </group>
    </>
  );
}

/** Applies the store's tunable camera pose (height/position/tilt) to the minimap
 *  camera, live. Lives inside the Canvas so it can reach the R3F camera.
 *
 *  `tilt` is a downward PITCH ANGLE in degrees (0 = looking straight ahead, 90 =
 *  straight down). The look-at target is derived from the angle so the camera
 *  always faces FORWARD (-Z) and down — it can never flip/invert the way a raw
 *  look-at-Z target could when it crossed the camera's own position. */
/** Debug handle for the minimap's own context — same family as
 *  `window.__synapseScene` (which only covers the MAIN canvas). Lets a perf
 *  question about the minimap be answered by rendering it manually and timing
 *  it, without guessing from draw-call counts. */
function MinimapDebugHandle() {
  const gl = useThree(s => s.gl);
  const scene = useThree(s => s.scene);
  const camera = useThree(s => s.camera);
  useEffect(() => {
    (window as unknown as { __synapseMinimap?: unknown }).__synapseMinimap = { gl, scene, camera };
    return () => { delete (window as unknown as { __synapseMinimap?: unknown }).__synapseMinimap; };
  }, [gl, scene, camera]);
  return null;
}

function MinimapCameraRig() {
  const { camera } = useThree();
  const cam = useFlowStore(s => s.minimapCam);
  useEffect(() => {
    const D = 8;                                   // look-ahead distance (orientation only)
    const pitch = THREE.MathUtils.degToRad(cam.tilt);
    camera.position.set(0, cam.height, cam.posZ);
    camera.lookAt(
      0,
      cam.height - Math.sin(pitch) * D,            // always below the camera
      cam.posZ   - Math.cos(pitch) * D,            // always ahead of the camera (-Z)
    );
    camera.updateProjectionMatrix();
  }, [camera, cam.height, cam.posZ, cam.tilt]);
  return null;
}

export default function FlowMinimap() {
  const seek = useFlowStore(s => s.seek);
  // Active perf preset (user-editable): aa/dpr for this SECOND WebGL context,
  // and whether the scene keeps updating during playback.
  const presetAa = useEditorStore(s => s.performanceSettings[s.perfMode].antialias);
  const presetDprCap = useEditorStore(s => s.performanceSettings[s.perfMode].dprCap);
  const minimapLive = useEditorStore(s => s.performanceSettings[s.perfMode].flowFx.minimapLiveDuringPlay);
  POTATO_MINIMAP_IDLE = !minimapLive;
  // Re-render (and recolour materials) when the theme changes.
  const themeAccent = useTheme(s => s.accent);
  const themeAccentHex = useTheme(s => s.accentHex);
  const themeTint = useTheme(s => s.tint);
  const themeTintHex = useTheme(s => s.tintHex);
  const cols = useMemo(() => ({
    deck:    tintHex(0.12, 0.022),
    divider: tintHex(0.45, 0.06),
    // HIT (trigger) line = the canonical design-system accent (oklch 0.70 0.205
    // at the live accent hue), same token as the rest of the accent UI — NOT a
    // custom lighter/desaturated shade. Follows the theme when the hue changes.
    hit:     accentHex(0.70, 0.205),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [themeAccent, themeAccentHex, themeTint, themeTintHex]);

  const handleClick = (e: ThreeEvent<MouseEvent>) => {
    // Floor click → world z → beat. Notes at beat B sit at screen-z
    // (currentBeat - B)*ZPB, so B = currentBeat - z/ZPB.
    const s = useFlowStore.getState();
    const zpb = ZPB * (s.flowInterfaceSettings.minimapZoom ?? 1);
    const beat = Math.max(0, s.currentBeat - e.point.z / zpb);
    seek((beat / Math.max(1, s.chart.bpm)) * 60 + s.chart.offset_seconds);
  };

  // Wheel over the minimap zooms it (reviewer ask: no settings trip — scroll
  // in for detail on the current section, out for the bird's-eye view).
  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
    const s = useFlowStore.getState();
    const cur = s.flowInterfaceSettings.minimapZoom ?? 1;
    const next = Math.max(0.4, Math.min(6, cur * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
    s.setFlowInterfaceSettings({ minimapZoom: Math.round(next * 100) / 100 });
  };

  return (
    <div style={minimapWrapStyle} onWheel={handleWheel}>
      {/* Initial camera pose — immediately overridden by MinimapCameraRig from
          the store (height / position / tilt, adjustable via the camera window). */}
      <Canvas
        // MSAA is a context-creation flag on this second context too (a fresh
        // context → the key remounts when the preset's aa flips).
        key={presetAa ? 'aa' : 'no-aa'}
        camera={{ position: [0, 7.4, 1.7], fov: 42, near: 0.1, far: 100 }}
        gl={{ antialias: presetAa }}
        // ~163 CSS px wide — dpr 2 doubles the fill for pills nobody can tell
        // apart. Same cap as the VR minimap; a preset dprCap clamps it.
        dpr={presetDprCap != null ? Math.max(0.5, Math.min(1.5, presetDprCap)) : [1, 1.5]}
        style={{ display: 'block', width: '100%', height: '100%' }}
      >
        {/* Live camera pose from the store (adjustable via the Minimap Camera window). */}
        <MinimapCameraRig />
        <MinimapDebugHandle />
        <MinimapScene deck={cols.deck} hit={cols.hit} divider={cols.divider} />
        {/* Invisible click-catcher floor spanning the visible track. */}
        <mesh position={[0, 0, -10]} rotation={[-Math.PI / 2, 0, 0]} onClick={handleClick}>
          <planeGeometry args={[TRACK_W + 1, 60]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      </Canvas>
    </div>
  );
}

